import { useNavigate } from 'react-router-dom';
import { UserPlus, Calendar, Users } from 'lucide-react';
import { useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';

export function LandingPage() {
  const navigate = useNavigate();
  const [userName, setUserName] = useState<string>('');
  const [userRoles, setUserRoles] = useState<string[]>([]);

  useEffect(() => {
    const getUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user?.email && window.location.pathname !== '/auth/login') {
        setUserName(user.email.split('@')[0]);
        
        // Fetch user roles
        const { data: roles, error } = await supabase
          .from('user_roles')
          .select('role_name')
          .eq('user_uuid', user.id);
        
        if (!error && roles) {
          setUserRoles(roles.map(role => role.role_name));
        }
      } else {
        setUserName('');
        setUserRoles([]);
      }
    };
    getUser();
    
    const { data: authListener } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') {
        setUserName('');
        setUserRoles([]);
      }
    });

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, []);

  return (
    <div className="max-w-6xl mx-auto">
      <div className="text-center mb-16">
        <h1 className="text-5xl font-bold mb-6 bg-gradient-to-r from-[#4CAF50] to-[#45a049] bg-clip-text text-transparent">
          VINCER Coordinator Dashboard
        </h1>
      </div>
      
      <div className="bg-white border rounded-xl p-6 mb-16 flex items-center gap-6">
        <div className="bg-[#E8F5E9] p-4 rounded-lg">
          <Users className="w-8 h-8 text-[#4CAF50]" />
        </div>
        <div>
          <h2 className="text-2xl font-semibold mb-2">
            Welcome back{userName ? `, ${userName}` : ''}! 👋
          </h2>
          <p className="text-gray-600">
            Ready to make some meaningful connections today? Manage your mentors, mentees, and meetings all in one place.
          </p>
          {userRoles.length > 0 && (
            <p className="text-sm text-gray-500 mt-2">
              Your roles: {userRoles.join(', ')}
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {/* Add Mentors CTA */}
        <div className="bg-white p-8 rounded-xl shadow-sm border hover:shadow-md transition-shadow">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#E8F5E9] mb-6">
              <UserPlus className="w-8 h-8 text-[#4CAF50]" />
            </div>
            <h2 className="text-2xl font-bold mb-4">Add Mentors</h2>
            <p className="text-gray-600 mb-6">
              Bring in experienced professionals to guide and support our mentees.
            </p>
            <button
              onClick={() => navigate('/coordinator/mentors')}
              className="w-full bg-[#4CAF50] text-white font-medium px-6 py-3 rounded-lg hover:bg-[#45a049] transition-colors"
            >
              Add New Mentor
            </button>
          </div>
        </div>

        {/* Add Mentees CTA */}
        <div className="bg-white p-8 rounded-xl shadow-sm border hover:shadow-md transition-shadow">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#E8F5E9] mb-6">
              <Users className="w-8 h-8 text-[#4CAF50]" />
            </div>
            <h2 className="text-2xl font-bold mb-4">Add Mentees</h2>
            <p className="text-gray-600 mb-6">
              Welcome new participants looking for guidance and growth opportunities.
            </p>
            <button
              onClick={() => navigate('/coordinator/mentees')}
              className="w-full bg-[#4CAF50] text-white font-medium px-6 py-3 rounded-lg hover:bg-[#45a049] transition-colors"
            >
              Add New Mentee
            </button>
          </div>
        </div>

        {/* Manage Meetings CTA */}
        <div className="bg-white p-8 rounded-xl shadow-sm border hover:shadow-md transition-shadow">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#E8F5E9] mb-6">
              <Calendar className="w-8 h-8 text-[#4CAF50]" />
            </div>
            <h2 className="text-2xl font-bold mb-4">Manage Meetings</h2>
            <p className="text-gray-600 mb-6">
              Schedule and oversee mentorship sessions and track progress.
            </p>
            <button
              onClick={() => navigate('/coordinator/meetings')}
              className="w-full bg-[#4CAF50] text-white font-medium px-6 py-3 rounded-lg hover:bg-[#45a049] transition-colors"
            >
              View Meetings
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}